import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  type MouseEvent,
} from 'react';
import type { ComponentProps } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { useWatchlist, useWatchlists } from '@/hooks/useWatchlist';
import useUser from '@/hooks/useUser';

import { doesCompanyExistInWatchlist } from '@/pages/Watchlist/utils';
import { useWatchlistMutations } from '@/pages/Watchlist/hooks/useWatchlistMutations';

import AddRemoveCompanyButton from './components/AddRemoveCompanyButton';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);

const LimitModal = loadable(
  () => import('@/pages/Watchlist/components/LimitModal')
);
const AddToWatchlistsModal = loadable(
  () => import('./components/AddToWatchlistsModal')
);

type Props = Omit<
  ComponentProps<typeof AddRemoveCompanyButton>,
  'isLoading' | 'existsInWatchlist' | 'minimal'
> & {
  companyId: string;
  tickerSymbol: string;
  minimal?: boolean;
  disabled?: boolean;
  onClick?: (e: MouseEvent) => void;
};

type Modal =
  | 'company-limit-modal'
  | 'guest-registration-modal'
  | 'add-to-watchlists-modal';

const WatchlistAddRemoveCompanyToggle = ({
  disabled,
  companyId,
  tickerSymbol,
  minimal = false,
  styleType = 'louder',
  onClick,
  ...rest
}: Props) => {
  const { data: user } = useUser();
  const { trackEvent } = useTracking();
  const [modal, setModal] = useState<Modal | null>(null);
  const { data: watchlist, isFetched: isWatchlistFetched } = useWatchlist();
  const { data: watchlists, isFetched: isWatchlistsFetched } = useWatchlists();
  const { addCompany, addCompanyStatus, removeCompany, removeCompanyStatus } =
    useWatchlistMutations();

  const hasFetched = isWatchlistFetched && isWatchlistsFetched;
  const hasMultipleWatchlists =
    typeof watchlists !== 'undefined' && watchlists.length > 1;
  const isLoading =
    addCompanyStatus === 'pending' || removeCompanyStatus === 'pending';

  const existsInSomeWatchlist = useMemo(() => {
    const watchlistKeys = watchlist?.items.map((n) => n.companyId);
    const watchlistsKeys = watchlists?.flatMap((w) =>
      w.items.map((n) => n.companyId)
    );

    return doesCompanyExistInWatchlist(
      companyId,
      hasMultipleWatchlists ? watchlistsKeys : watchlistKeys
    );
  }, [companyId, hasMultipleWatchlists, watchlist, watchlists]);

  const handleLoggedOutAddClick = useCallback(() => {
    setModal('guest-registration-modal');
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: 'addToWatchlists',
      subject: 'guestRegistrationModal',
    });
  }, [trackEvent]);

  const handleRemoveCompany = useCallback(() => {
    if (typeof watchlist === 'undefined') {
      return;
    }
    removeCompany({
      companyId,
      watchlistId: watchlist?.id,
    });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'remove',
      action: 'click',
    });
  }, [trackEvent, removeCompany, companyId, watchlist]);

  const handleAddCompany = useCallback(() => {
    if (typeof watchlist === 'undefined') {
      return;
    }
    addCompany({
      id: companyId,
      watchlistId: watchlist?.id,
    });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'add',
      action: 'click',
    });
  }, [trackEvent, addCompany, companyId, watchlist]);

  useEffect(() => {
    if (user.loggedIn && modal === 'guest-registration-modal') {
      // The user must have logged in or registered via the modal
      // So we hide the modal
      setModal(null);

      // If the user has multiple watchlists, show the 'add to watchlists' modal
      // Otherwise, add the company to their watchlist
      if (hasMultipleWatchlists) {
        setModal('add-to-watchlists-modal');
      } else if (watchlist) {
        addCompany({
          id: companyId,
          watchlistId: watchlist?.id,
        });
      }
    }
  }, [
    user.loggedIn,
    modal,
    addCompany,
    companyId,
    watchlist,
    hasMultipleWatchlists,
  ]);

  return (
    <>
      <div
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
        }}
      >
        {modal === 'guest-registration-modal' && (
          <GuestRegistrationModal
            canClose={true}
            removeWindowScroll={true}
            onClose={() => setModal(null)}
            onRegisterSuccess={() => {
              trackEvent({
                type: 'track',
                subject: 'addToWatchLists',
                modifier: 'registration',
                action: 'success',
              });
            }}
          />
        )}
        {modal === 'company-limit-modal' && watchlist && (
          <LimitModal watchlist={watchlist} onClose={() => setModal(null)} />
        )}
        {modal === 'add-to-watchlists-modal' && watchlists && (
          <AddToWatchlistsModal
            data-cy-id="add-remove-to-watchlists-modal"
            companyId={companyId}
            tickerSymbol={tickerSymbol}
            watchlists={watchlists}
            onClose={() => setModal(null)}
          />
        )}
      </div>
      <AddRemoveCompanyButton
        minimal={minimal}
        isLoading={isLoading}
        styleType={styleType}
        existsInWatchlist={existsInSomeWatchlist}
        disabled={disabled || !hasFetched || isLoading}
        companyId={companyId}
        data-cy-id="add-remove-to-watchlist-toggle"
        onClick={(e: MouseEvent) => {
          onClick?.(e);
          if (!user.loggedIn) {
            handleLoggedOutAddClick();
          } else if (hasMultipleWatchlists) {
            setModal('add-to-watchlists-modal');
          } else if (existsInSomeWatchlist) {
            handleRemoveCompany();
          } else if (
            watchlist &&
            watchlist.items.length === watchlist.itemsLimit
          ) {
            setModal('company-limit-modal');
          } else {
            handleAddCompany();
          }
        }}
        {...rest}
      />
    </>
  );
};

export default WatchlistAddRemoveCompanyToggle;
